import { useCallback } from 'react';
import { DateTime } from 'luxon';
import { IconApproved, IconCancel } from 'assets/img';
import { PrimaryButton, SecondaryButton, useDialog } from 'ogp/providers/dialog-provider';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAlert } from 'shared/components/alerts';
import { useTranslation } from 'react-i18next';
import { useHqRelativePath } from 'hq/hooks/useHqRelativePath';
import { hqRoutes } from 'hq/Routes';
import { CreateDocumentParams } from 'hq/services/documents-service.types';
import { documentAlerts } from 'shared/components/alerts/alert-messages';
import { useAddDocument } from './use-add-document';
import { useGetDocumentForFw } from './use-find-document-for-fw';
import { DocumentFormTypes } from '../form/document-form';
import { DocumentType } from '../documents-page/table/documents-table.types';

export const useAddDocumentFormActions = () => {
  const navigate = useNavigate();
  const generatePath = useHqRelativePath();
  const redirectPath = generatePath(hqRoutes.DocumentsPage, undefined);

  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const { alertSuccess, alertError } = useAlert();
  const { t } = useTranslation(['web-common', 'hq-screen-specific']);
  const mutation = useAddDocument();
  const { watch } = useFormContext<DocumentFormTypes>();

  const { flexWorkerOption, endDate, documentType, startDate } = watch();
  const flexWorkerId = flexWorkerOption?.value;

  const { data } = useGetDocumentForFw(flexWorkerId as string, documentType as DocumentType);
  const isDocumentExist = !!data;

  const submitDisabled = !flexWorkerId || !documentType || !startDate;

  const handleAddDocument = useCallback(() => {
    if (!flexWorkerId || !documentType || !startDate) {
      return;
    }

    openDialog(
      {
        title: isDocumentExist
          ? t('document_create.confirmation_dialog_title_existing', { ns: 'hq-screen-specific' })
          : t('document_create.confirmation_dialog_title_new', { ns: 'hq-screen-specific' }),
        secondaryButton: (props) => (
          <SecondaryButton iconColorMode="fill" startIcon={<IconCancel />} {...props}>
            {t('buttons.cancel', { ns: 'web-common' })}
          </SecondaryButton>
        ),
        primaryButton: (props) => (
          <PrimaryButton startIcon={<IconApproved />} {...props}>
            {t('buttons.confirm', { ns: 'web-common' })}
          </PrimaryButton>
        ),
      },
      () => {
        disableDialog();

        const endDateAsDateTime = endDate ? DateTime.fromISO(endDate) : null;
        const startDateAsDateTime = DateTime.fromISO(startDate);

        const mutationData: CreateDocumentParams = {
          flexWorkerId,
          documentType: documentType,
          startDate: startDateAsDateTime,
          endDate: endDateAsDateTime ?? null,
        };

        mutation.mutate(mutationData, {
          onSuccess: () => {
            closeDialog();
            alertSuccess(t(documentAlerts.success.added, { ns: 'hq-screen-specific' }));
            navigate(redirectPath);
          },
          onError: (e) => {
            alertError(e);
          },
          onSettled: () => {
            enableDialog();
          },
        });
      }
    );
  }, [
    flexWorkerId,
    documentType,
    startDate,
    endDate,
    isDocumentExist,
    openDialog,
    closeDialog,
    disableDialog,
    enableDialog,
    t,
    mutation,
    alertSuccess,
    alertError,
    navigate,
    redirectPath,
  ]);

  const handleCancel = useCallback(() => {
    navigate(redirectPath);
  }, [navigate, redirectPath]);

  return {
    handleAddDocument,
    handleCancel,
    submitDisabled,
  };
};
